import { useState } from "react"

import JSON5 from "json5"

import { logger } from "@/lib/logger"

const log = logger("LocalStorage")
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T) => void] {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === "undefined") {
      return initialValue
    }

    try {
      const item = window.localStorage.getItem(key)
      return item ? JSON5.parse(item) : initialValue
    } catch (error: unknown) {
      log.warn(`Error reading localStorage key "${key}"`, { error, key })
      return initialValue
    }
  })

  // Return a wrapped version of useState's setter function that persists the new value to localStorage
  const setValue = (value: T) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore: T =
        value instanceof Function
          ? (value as (prev: T) => T)(storedValue)
          : value
      setStoredValue(valueToStore)
      if (typeof window !== "undefined") {
        window.localStorage.setItem(key, JSON.stringify(valueToStore))
      }
    } catch (error: unknown) {
      log.warn(`Error setting localStorage key "${key}"`, { error, key })
    }
  }

  return [storedValue, setValue]
}
